import React, {
	useCallback,
	type SyntheticEvent,
	type KeyboardEvent,
	forwardRef,
	type Ref,
} from 'react';
import { CustomThemeButton } from '@atlaskit/button';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import { N40 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const DeleteButton = forwardRef(
	({ attachmentId, onDeleteAttachment }: Props, ref: Ref<HTMLElement> | undefined) => {
		const intl = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const onClick = useCallback(
			(event: SyntheticEvent<HTMLElement>) => {
				event.stopPropagation();
				const analyticsEvent = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				});
				fireUIAnalytics(analyticsEvent, 'deleteAttachment');
				onDeleteAttachment(attachmentId);
			},
			[attachmentId, createAnalyticsEvent, onDeleteAttachment],
		);

		const onKeyPress = useCallback((event: KeyboardEvent) => {
			if (event.key === 'Enter') {
				event.stopPropagation();
			}
		}, []);

		return (
			<CustomThemeButton
				ref={ref}
				testId={`issue-attachments-table.ui.delete-button.custom-theme-button.${attachmentId}`}
				appearance="subtle"
				iconAfter={<TrashIcon label={intl.formatMessage(messages.delete)} />}
				onClick={onClick}
				onKeyPress={onKeyPress}
				// @ts-expect-error - TS2322 - Type '(current: (props: ThemeProps) => ThemeTokens, themeProps: ThemeProps) => { accentColor?: string | string[] | undefined; alignContent?: string | string[] | undefined; ... 909 more ...; buttonStyles: { ...; }; }' is not assignable to type '(current: (props: ThemeProps) => ThemeTokens, props: ThemeProps) => ThemeTokens'.
				theme={(current, themeProps) => {
					const { buttonStyles, spinnerStyles } = current(themeProps);
					return {
						buttonStyles: {
							...buttonStyles,
							display: 'flex',
							justifyContent: 'center',
							'&:hover': {
								background: token('color.background.neutral.hovered', N40),
							},
							margin: '0',
						},
						...spinnerStyles,
					};
				}}
				interactionName="auto-generated.jira-issue-attachments-table.ui.delete-button.button"
			/>
		);
	},
);
export default DeleteButton;
